<template>

    <component :is="component.is" class="anchor" :class="{ '--underlined': underlined, 'font-bold': bold }" v-bind="component.params">

        <slot v-if="underlined"/>

        <slot v-else/>

        <div v-if="showArrow" class="ml-2 inline">
            →
        </div>

    </component>

</template>

<script lang="ts">

    import { computed, defineComponent, PropType } from 'vue'
    import { RouteLocation } from 'vue-router'
    import { useTranslation } from '../compositions/UseTranslation'
    import { Locale } from '../enums/Locale'

    export default defineComponent({
        name: 'Anchor',
        props: {
            route: { type: [ Object, String ] as PropType<RouteLocation | string>, required: true },
            underlined: { type: Boolean, default: false },
            mailTo: { type: Boolean, default: false },
            telephone: { type: Boolean, default: false },
            external: { type: Boolean, default: false },
            showArrow: { type: Boolean, default: false },
            bold: { type: Boolean, default: true },
            purple: { type: Boolean, default: false }
        },
        setup(props) {

            const { isCNLocale } = useTranslation()

            const routeParameters = computed(() => {

                const name = props.route.name
                const parameters = props.route.params
                const locale = isCNLocale ? Locale.CHINESE : ''
                const routeName = name || props.route

                return {
                    name: routeName,
                    params: { locale, ...parameters }
                }

            })

            const externalHref = computed(() => {

                if (props.mailTo) {

                    return `mailto:${ props.route }`

                }

                if (props.telephone) {

                    return `tel:${ props.route }`

                }

                return props.route

            })

            return {

                component: computed(() => {

                    if (!props.mailTo && !props.telephone && !props.external) {

                        return {
                            is: 'router-link',
                            params: {
                                to: routeParameters.value
                            }
                        }

                    }

                    return {
                        is: 'a',
                        params: {
                            href: externalHref.value,
                            target: '_blank',
                            rel: 'noopener'
                        }
                    }

                })

            }

        }
    })

</script>

<style lang="scss">

    @include component(anchor) {

        @apply relative inline-flex items-center text-base font-body;

        @include option(underlined) {

            @apply relative pb-2 inline-flex;

            width: fit-content;

            &:after {

                width: 0%;
                height: 1px;
                content: '';

                @apply transition-all duration-500 bottom-0 absolute bg-orange;

            }

            &:hover:after {

                width: 100%;

            }

            &:before {

                position: absolute;
                bottom: 0;
                display: block;
                width: 100%;
                height: 1px;

                /**
                 * Move the opacity to the theme, so when it is dark use 0.2, 1 otherwise
                 */

                opacity: 1;
                animation: mask-marquee 30s infinite forwards linear;
                content: '';
                mask-image: url('../../public/assets/bottom-border.svg');
                mask-size: contain;

                @apply bg-orange;

            }

            @keyframes mask-marquee {

                0% {

                    mask-position: 0;

                }

                100% {

                    mask-position: 100%;

                }

            }

        }

    }

</style>
